import { useCallback, useEffect, useMemo, useState } from 'react'
import Alert from 'react-bootstrap/Alert'
import Button from 'react-bootstrap/Button'
import Card from 'react-bootstrap/Card'
import Col from 'react-bootstrap/Col'
import Form from 'react-bootstrap/Form'
import Row from 'react-bootstrap/Row'
import Stack from 'react-bootstrap/Stack'
import Table from 'react-bootstrap/Table'
import DatePicker from 'react-datepicker'

import AuthService from '../../utilities/services/auth.service'
import {
  capitalizeString,
  checkPermission,
  formatDateTime,
} from '../../utilities/helpers'
import {
  createReportBuilderReport,
  deleteReportBuilderReport,
  exportReportBuilder,
  getReportBuilderFieldCatalog,
  getReportBuilderReport,
  getReportBuilderReports,
  previewReportBuilder,
  runReportBuilderReport,
  updateReportBuilderReport,
} from '../../utilities/api'
import { useReportRefresh } from '../../hooks/useReportRefresh'
import {
  ORGANIZATION_PERMISSION_SLUGS,
  constants,
} from '@blocktickets/shared-utils'
import { NoPermissionsContainer } from '../NoPermissionsContainer'
import { PageLoadingContainer } from '../PageLoadingContainer'
import { ReportRefreshButton } from '../ReportRefreshButton'
import AskForReportSection from '../ReportsWrapper/AskForReportSection'
import { buildReportBuilderFormFromAiPlan } from '../ReportsWrapper/aiPlanToReportBuilderForm'
import {
  ReportProgressIndicator,
  useReportProgress,
  REPORT_PROGRESS_STAGES,
  EXPORT_FORMAT_STAGE_LABELS,
  isAbortError,
} from '../ReportProgressIndicator'

import '../AccessControlWrapper/report-print.scss'
import './reportBuilderWrapper.scss'

const FILTER_OPERATORS = [
  { value: 'eq', label: '=' },
  { value: 'neq', label: '!=' },
  { value: 'contains', label: 'contains' },
  { value: 'starts_with', label: 'starts with' },
  { value: 'in', label: 'in (comma separated)' },
  { value: 'gt', label: '>' },
  { value: 'gte', label: '>=' },
  { value: 'lt', label: '<' },
  { value: 'lte', label: '<=' },
  { value: 'between', label: 'between (comma separated)' },
  { value: 'is_null', label: 'is null' },
  { value: 'not_null', label: 'is not null' },
]

const SORT_DIRECTIONS = [
  { value: 'asc', label: 'Ascending' },
  { value: 'desc', label: 'Descending' },
]

const AGGREGATION_OPTIONS = [
  { value: 'count', label: 'Count' },
  { value: 'sum', label: 'Sum' },
  { value: 'avg', label: 'Average' },
  { value: 'min', label: 'Min' },
  { value: 'max', label: 'Max' },
]

const { ORDER_SOURCE, ORDER_SOURCE_LABEL, PAYMENT_TYPE, PAYMENT_TYPE_LABEL } =
  constants

const { PRODUCT_TYPE, PRODUCT_TYPE_LABEL } = constants

const formatEnumLabel = (value, labelMap = {}) =>
  labelMap[value] || capitalizeString(String(value || '').replace(/_/g, ' '))

const SOURCE_FILTER_OPTIONS = Object.values(ORDER_SOURCE).map((value) => ({
  value,
  label: formatEnumLabel(value, ORDER_SOURCE_LABEL),
}))

const PAYMENT_TYPE_FILTER_OPTIONS = Object.values(PAYMENT_TYPE).map(
  (value) => ({
    value,
    label: formatEnumLabel(value, PAYMENT_TYPE_LABEL),
  })
)

const PRODUCT_TYPE_FILTER_OPTIONS = Object.values(PRODUCT_TYPE).map(
  (value) => ({
    value,
    label: formatEnumLabel(value, PRODUCT_TYPE_LABEL),
  })
)

const MULTI_SELECT_FILTER_OPTIONS_BY_FIELD = {
  source: SOURCE_FILTER_OPTIONS,
  payment_type: PAYMENT_TYPE_FILTER_OPTIONS,
  payment_option: PAYMENT_TYPE_FILTER_OPTIONS,
  product_type: PRODUCT_TYPE_FILTER_OPTIONS,
}

const isMultiSelectFilterField = (fieldId) =>
  Boolean(MULTI_SELECT_FILTER_OPTIONS_BY_FIELD[String(fieldId || '')])

const DATE_FILTER_FIELD_IDS = new Set(['processed_at', 'date_paid'])

const isDateFilterField = (fieldId) =>
  DATE_FILTER_FIELD_IDS.has(String(fieldId || ''))

const formatDateForFilter = (date) => {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return ''
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  const year = date.getFullYear()
  return `${month}/${day}/${year}`
}

const parseDateFilterValue = (value) => {
  if (!value || Array.isArray(value)) return null
  const normalized = String(value).trim()
  if (!normalized) return null

  const mmDdYyyy = normalized.match(/^(\d{2})\/(\d{2})\/(\d{4})$/)
  if (mmDdYyyy) {
    const month = Number(mmDdYyyy[1])
    const day = Number(mmDdYyyy[2])
    const year = Number(mmDdYyyy[3])
    const parsed = new Date(year, month - 1, day)
    if (
      parsed.getFullYear() === year &&
      parsed.getMonth() === month - 1 &&
      parsed.getDate() === day
    ) {
      return parsed
    }
    return null
  }

  const timestamp = Date.parse(normalized)
  if (Number.isNaN(timestamp)) return null
  return new Date(timestamp)
}

const DEFAULT_FORM = {
  name: '',
  description: '',
  selectedFields: [],
  filters: [],
  sorts: [],
  groupBy: [],
  aggregations: [],
  schedule: {
    enabled: false,
    frequency: 'daily',
    dayOfWeek: 1,
    hour: 8,
    minute: 0,
    timezone: 'America/New_York',
    email: '',
  },
  exportFormats: ['csv', 'xlsx', 'pdf'],
}

const WEEK_DAY_OPTIONS = [
  { value: 0, label: 'Sunday' },
  { value: 1, label: 'Monday' },
  { value: 2, label: 'Tuesday' },
  { value: 3, label: 'Wednesday' },
  { value: 4, label: 'Thursday' },
  { value: 5, label: 'Friday' },
  { value: 6, label: 'Saturday' },
]

const getDefaultFilter = (fieldId = '') => ({
  fieldId,
  operator: 'eq',
  value: '',
})

const getDefaultSort = (fieldId = '') => ({
  fieldId,
  direction: 'asc',
})

const getDefaultAggregation = () => ({
  fieldId: '',
  fn: 'sum',
  alias: '',
})

const normalizeMultiSelectFilterValue = (rawValue) => {
  if (Array.isArray(rawValue)) return rawValue.map((value) => String(value))
  if (rawValue == null || rawValue === '') return []
  return [String(rawValue)]
}

const normalizeFilterForForm = (filter = {}) => {
  const fieldId = String(filter.fieldId || '')
  const isMultiSelectFilter = isMultiSelectFilterField(fieldId)

  return {
    fieldId,
    operator: isMultiSelectFilter ? 'in' : filter.operator || 'eq',
    value: isMultiSelectFilter
      ? normalizeMultiSelectFilterValue(filter.value)
      : (filter.value ?? ''),
  }
}

const parseFilterValue = (operator, rawValue) => {
  if (operator === 'is_null' || operator === 'not_null') return null
  if (operator === 'in' || operator === 'between') {
    if (Array.isArray(rawValue)) {
      return rawValue.map((value) => String(value).trim()).filter(Boolean)
    }
    return String(rawValue || '')
      .split(',')
      .map((x) => x.trim())
      .filter(Boolean)
  }
  return rawValue
}

const mapReportToForm = (report) => ({
  id: report.id,
  name: report.name || '',
  description: report.description || '',
  selectedFields: report.definition?.selectedFields || [],
  filters: (report.definition?.filters || []).map(normalizeFilterForForm),
  sorts: report.definition?.sorts || [],
  groupBy: report.definition?.groupBy || [],
  aggregations: report.definition?.aggregations || [],
  schedule: report.schedule || DEFAULT_FORM.schedule,
  exportFormats: report.exportFormats || DEFAULT_FORM.exportFormats,
})

const formatCellValue = (value) => {
  if (value == null) return ''
  if (typeof value === 'number') return Number.isFinite(value) ? value : ''
  if (typeof value === 'boolean') return value ? 'Yes' : 'No'
  const asDate = Date.parse(value)
  if (!Number.isNaN(asDate) && String(value).includes('-')) {
    return formatDateTime(value)
  }
  return String(value)
}

const downloadBlobFromResponse = (blob, filename) => {
  const link = document.createElement('a')
  link.href = URL.createObjectURL(blob)
  link.download = filename
  link.click()
  URL.revokeObjectURL(link.href)
}

export default function ReportBuilderWrapper() {
  const organization = AuthService.getOrg()?.[0]
  const permissions = AuthService.getPermissions()
  const canUseBuilder =
    checkPermission(
      permissions,
      ORGANIZATION_PERMISSION_SLUGS.VIEW_REPORT_BUILDER
    ) ||
    checkPermission(
      permissions,
      ORGANIZATION_PERMISSION_SLUGS.VIEW_ORDER_LISTINGS
    )

  const [catalog, setCatalog] = useState([])
  const [reports, setReports] = useState([])
  const [form, setForm] = useState(DEFAULT_FORM)
  const [selectedReportId, setSelectedReportId] = useState(null)
  const [previewRows, setPreviewRows] = useState([])
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [previewing, setPreviewing] = useState(false)
  const [exporting, setExporting] = useState(false)
  const { refreshToken, refresh, completeRefresh } =
    useReportRefresh(previewing)
  const [status, setStatus] = useState('')
  const [error, setError] = useState('')
  const [newFieldId, setNewFieldId] = useState('')
  const [aiMappingWarnings, setAiMappingWarnings] = useState([])
  const [aiSummary, setAiSummary] = useState('')
  const progress = useReportProgress()
  const isGenerating = previewing || exporting

  const catalogById = useMemo(() => {
    const map = new Map()
    catalog.forEach((field) => map.set(field.id, field))
    return map
  }, [catalog])

  const selectedFieldOptions = useMemo(
    () =>
      catalog.filter(
        (field) => !form.selectedFields.some((id) => id === field.id)
      ),
    [catalog, form.selectedFields]
  )

  const loadInitialData = async () => {
    if (!organization?.uuid) return
    setLoading(true)
    setError('')
    try {
      const [catalogRes, reportsRes] = await Promise.all([
        getReportBuilderFieldCatalog(),
        getReportBuilderReports(organization.uuid),
      ])
      setCatalog(catalogRes.data?.fields || [])
      setReports(reportsRes.data?.reports || [])
    } catch (err) {
      setError(
        err?.response?.data?.error?.message || 'Failed loading report builder'
      )
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadInitialData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organization?.uuid])

  const buildDefinitionFromFormData = (formData) => ({
    selectedFields: formData.selectedFields,
    filters: formData.filters.map((filter) => ({
      fieldId: filter.fieldId,
      operator: isMultiSelectFilterField(filter.fieldId)
        ? 'in'
        : filter.operator,
      value: parseFilterValue(
        isMultiSelectFilterField(filter.fieldId) ? 'in' : filter.operator,
        filter.value
      ),
    })),
    sorts: formData.sorts,
    groupBy: formData.groupBy.filter((fieldId) => String(fieldId || '').trim()),
    aggregations: formData.aggregations,
  })

  const buildDefinitionPayload = () => buildDefinitionFromFormData(form)

  const handleApplyAiPlan = useCallback(
    async (plan, meta = {}) => {
      if (!organization?.uuid) {
        throw new Error('Organization is required')
      }

      const mapped = buildReportBuilderFormFromAiPlan(plan, {
        from: plan.from,
        to: plan.to,
        rangeLabel: plan.rangeLabel,
        catalogFieldIds: catalog.map((field) => field.id),
      })

      const nextForm = {
        ...DEFAULT_FORM,
        name: mapped.name,
        description: mapped.description,
        selectedFields: mapped.selectedFields,
        filters: mapped.filters.map(normalizeFilterForForm),
        sorts: mapped.sorts,
        groupBy: mapped.groupBy,
        aggregations: mapped.aggregations,
      }

      setAiMappingWarnings(mapped.warnings)
      setAiSummary(String(meta?.answerText || '').trim())
      setSelectedReportId(null)
      setForm(nextForm)
      setError('')
      setPreviewing(true)
      const controller = progress.start({
        stage: REPORT_PROGRESS_STAGES.PREPARING,
        message: 'Preparing Report Preview...',
      })

      try {
        progress.setStage(REPORT_PROGRESS_STAGES.LOADING_DATA)
        const response = await previewReportBuilder(
          {
            organizationUUID: organization.uuid,
            definition: buildDefinitionFromFormData(nextForm),
            limit: 100,
            offset: 0,
          },
          controller?.signal
        )
        const rows = response.data?.rows || []
        setPreviewRows(rows)
        setStatus(
          rows.length
            ? `AI report preview loaded (${response.data?.count ?? rows.length} rows). Save Report to keep it.`
            : 'AI report preview loaded with no rows for this period and filters.'
        )
        document
          .getElementById('report-builder-preview')
          ?.scrollIntoView({ behavior: 'smooth', block: 'start' })
      } catch (err) {
        if (isAbortError(err)) {
          setStatus('Preview cancelled')
          return
        }
        setPreviewRows([])
        setAiSummary('')
        throw err
      } finally {
        progress.complete()
        setPreviewing(false)
      }
    },
    [
      catalog,
      organization?.uuid,
      progress.start,
      progress.setStage,
      progress.complete,
    ]
  )

  const handlePreview = async () => {
    setPreviewing(true)
    setError('')
    setAiSummary('')
    const controller = progress.start({
      stage: REPORT_PROGRESS_STAGES.PREPARING,
      message: 'Preparing Report Preview...',
    })
    try {
      progress.setStage(REPORT_PROGRESS_STAGES.LOADING_DATA)
      const response = await previewReportBuilder(
        {
          organizationUUID: organization.uuid,
          definition: buildDefinitionPayload(),
          limit: 100,
          offset: 0,
        },
        controller?.signal
      )
      setPreviewRows(response.data?.rows || [])
      setStatus(`Preview loaded (${response.data?.count ?? 0} rows)`)
    } catch (err) {
      if (isAbortError(err)) {
        setStatus('Preview cancelled')
        return
      }
      setError(err?.response?.data?.error?.message || 'Could not load preview')
    } finally {
      progress.complete()
      setPreviewing(false)
      completeRefresh()
    }
  }
  const handleSave = async () => {
    if (!form.name.trim()) {
      setError('Report name is required')
      return
    }
    setSaving(true)
    setError('')
    setStatus('')
    const payload = {
      name: form.name.trim(),
      description: form.description.trim(),
      organizationUUID: organization.uuid,
      definition: buildDefinitionPayload(),
      schedule: form.schedule,
      exportFormats: form.exportFormats,
      isActive: true,
    }
    try {
      if (selectedReportId) {
        await updateReportBuilderReport(selectedReportId, payload)
        setStatus('Report updated')
      } else {
        const response = await createReportBuilderReport(payload)
        const created = response.data?.report
        if (created?.id) setSelectedReportId(created.id)
        setStatus('Report saved')
      }
      await loadInitialData()
    } catch (err) {
      setError(err?.response?.data?.error?.message || 'Could not save report')
    } finally {
      setSaving(false)
    }
  }

  const handleRunSaved = async () => {
    if (!selectedReportId) return
    setPreviewing(true)
    setError('')
    const controller = progress.start({
      stage: REPORT_PROGRESS_STAGES.PREPARING,
      message: 'Running Saved Report...',
    })
    try {
      progress.setStage(REPORT_PROGRESS_STAGES.LOADING_DATA)
      const response = await runReportBuilderReport(
        selectedReportId,
        organization.uuid,
        controller?.signal
      )
      setPreviewRows(response.data?.result?.rows || [])
      setStatus('Saved report executed')
    } catch (err) {
      if (isAbortError(err)) {
        setStatus('Run cancelled')
        return
      }
      setError(
        err?.response?.data?.error?.message || 'Could not run saved report'
      )
    } finally {
      progress.complete()
      setPreviewing(false)
      completeRefresh()
    }
  }

  useEffect(() => {
    if (!refreshToken) return
    if (previewRows.length > 0) {
      handlePreview()
    } else if (selectedReportId) {
      handleRunSaved()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshToken])

  const handleExport = async (format) => {
    setError('')
    setExporting(true)
    const controller = progress.start({
      stage: REPORT_PROGRESS_STAGES.PREPARING,
      message: 'Preparing Export...',
    })
    try {
      progress.setStage(
        REPORT_PROGRESS_STAGES.GENERATING_EXPORT,
        EXPORT_FORMAT_STAGE_LABELS[format] || EXPORT_FORMAT_STAGE_LABELS.csv
      )
      const response = await exportReportBuilder(
        {
          organizationUUID: organization.uuid,
          definition: buildDefinitionPayload(),
          format,
          fileName: form.name || 'custom-report',
        },
        controller?.signal
      )
      progress.setStage(REPORT_PROGRESS_STAGES.FINALIZING)
      const extension =
        format === 'xlsx' ? 'xlsx' : format === 'pdf' ? 'pdf' : 'csv'
      downloadBlobFromResponse(
        response.data,
        `${form.name || 'custom-report'}.${extension}`
      )
      setStatus(`Exported ${format.toUpperCase()}`)
    } catch (err) {
      if (isAbortError(err)) {
        setStatus('Export cancelled')
        return
      }
      setError(
        err?.response?.data?.error?.message || `Could not export ${format}`
      )
    } finally {
      progress.complete()
      setExporting(false)
    }
  }

  const handleCancelGeneration = () => {
    progress.cancel()
    setPreviewing(false)
    setExporting(false)
    setStatus('Request cancelled')
  }

  const loadSavedReport = async (report) => {
    setSelectedReportId(report.id)
    setPreviewRows([])
    setAiSummary('')
    setAiMappingWarnings([])
    setError('')
    setStatus('Loading report...')
    try {
      const response = await getReportBuilderReport(
        report.id,
        organization.uuid
      )
      const fullReport = response.data?.report || report
      setForm(mapReportToForm(fullReport))
      setStatus(`Loaded "${fullReport.name || report.name}"`)
    } catch (err) {
      setError(err?.response?.data?.error?.message || 'Could not load report')
      setStatus('')
    }
  }

  const handleDelete = async (reportId) => {
    setError('')
    try {
      await deleteReportBuilderReport(reportId, organization.uuid)
      if (selectedReportId === reportId) {
        setSelectedReportId(null)
        setForm(DEFAULT_FORM)
        setPreviewRows([])
      }
      await loadInitialData()
      setStatus('Report deleted')
    } catch (err) {
      setError(err?.response?.data?.error?.message || 'Could not delete report')
    }
  }

  if (loading) {
    return <PageLoadingContainer />
  }

  if (!canUseBuilder) {
    return <NoPermissionsContainer />
  }

  return (
    <section
      className="spacer-sm access-control-report access-control-report--v2 report-builder-page"
      id="analytics-wrapper"
    >
      {isGenerating && (
        <ReportProgressIndicator
          active
          variant="overlay"
          title={
            exporting ? 'Generating Export...' : 'Generating Report Preview...'
          }
          stage={progress.stage}
          message={progress.message}
          percent={progress.percent}
          onCancel={handleCancelGeneration}
        />
      )}
      <header className="section-header-sm report-page-header report-builder-page__header">
        <div className="section-heading">
          <h1>Report Builder</h1>
        </div>
        <p className="section-header-desc">
          Build orders-first reports with selectable fields, filters, grouping,
          and exports.
        </p>
      </header>

      {error && (
        <Alert className="report-builder-alert" variant="danger">
          {error}
        </Alert>
      )}
      {status && (
        <Alert className="report-builder-alert" variant="success">
          {status}
        </Alert>
      )}

      <AskForReportSection
        onApplyPlan={handleApplyAiPlan}
        mappingWarnings={aiMappingWarnings}
        filters={form.filters}
      />

      {aiSummary ? (
        <Alert
          className="report-builder-alert"
          variant="info"
          data-testid="report-builder-ai-summary"
        >
          <div className="fw-semibold mb-1">AI summary</div>
          {String(aiSummary)
            .split('\n')
            .map((line, index) => (
              <div key={index} className={index === 0 ? '' : 'small'}>
                {line}
              </div>
            ))}
        </Alert>
      ) : null}

      <Row className="g-3 report-builder-content-row">
        <Col lg={3}>
          <Card
            body
            className="card--md report-builder-card report-builder-card--saved"
          >
            <h5 className="mb-3">Saved Reports</h5>
            <Stack gap={2}>
              {reports.map((report) => (
                <div key={report.id} className="d-flex gap-2">
                  <Button
                    variant={
                      selectedReportId === report.id
                        ? 'primary'
                        : 'outline-primary'
                    }
                    className="w-100 text-start report-builder__saved-btn"
                    onClick={() => loadSavedReport(report)}
                  >
                    {report.name}
                  </Button>
                  <Button
                    variant="outline-danger"
                    className="report-builder__delete-btn"
                    onClick={() => handleDelete(report.id)}
                  >
                    Delete
                  </Button>
                </div>
              ))}
              {!reports.length && (
                <span className="text-muted caption">
                  No saved reports yet.
                </span>
              )}
            </Stack>
          </Card>
        </Col>

        <Col lg={9}>
          <Card body className="card--md mb-3 report-builder-card">
            <Row className="g-3">
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Report Name</Form.Label>
                  <Form.Control
                    value={form.name}
                    onChange={(event) =>
                      setForm((prev) => ({ ...prev, name: event.target.value }))
                    }
                    placeholder="e.g. Orders by Source"
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Description</Form.Label>
                  <Form.Control
                    value={form.description}
                    onChange={(event) =>
                      setForm((prev) => ({
                        ...prev,
                        description: event.target.value,
                      }))
                    }
                    placeholder="Optional description"
                  />
                </Form.Group>
              </Col>
            </Row>
          </Card>

          <Card body className="card--md mb-3 report-builder-card">
            <h5 className="mb-3">Selected Fields</h5>
            <Row className="g-2 align-items-end">
              <Col md={8}>
                <Form.Select
                  value={newFieldId}
                  onChange={(event) => setNewFieldId(event.target.value)}
                >
                  <option value="">Select a field...</option>
                  {selectedFieldOptions.map((field) => (
                    <option key={field.id} value={field.id}>
                      {field.label}
                    </option>
                  ))}
                </Form.Select>
              </Col>
              <Col md={4}>
                <Button
                  className="report-builder__primary-btn w-100"
                  onClick={() => {
                    if (!newFieldId) return
                    setForm((prev) => ({
                      ...prev,
                      selectedFields: [...prev.selectedFields, newFieldId],
                    }))
                    setNewFieldId('')
                  }}
                >
                  Add Field
                </Button>
              </Col>
            </Row>
            <Stack gap={2} className="mt-3">
              {form.selectedFields.map((fieldId, index) => (
                <div key={`${fieldId}-${index}`} className="d-flex gap-2">
                  <Form.Control
                    readOnly
                    value={catalogById.get(fieldId)?.label || fieldId}
                  />
                  <Button
                    variant="outline-primary"
                    disabled={index === 0}
                    onClick={() =>
                      setForm((prev) => {
                        const next = [...prev.selectedFields]
                        ;[next[index - 1], next[index]] = [
                          next[index],
                          next[index - 1],
                        ]
                        return { ...prev, selectedFields: next }
                      })
                    }
                  >
                    Up
                  </Button>
                  <Button
                    variant="outline-primary"
                    disabled={index === form.selectedFields.length - 1}
                    onClick={() =>
                      setForm((prev) => {
                        const next = [...prev.selectedFields]
                        ;[next[index + 1], next[index]] = [
                          next[index],
                          next[index + 1],
                        ]
                        return { ...prev, selectedFields: next }
                      })
                    }
                  >
                    Down
                  </Button>
                  <Button
                    variant="outline-danger"
                    onClick={() =>
                      setForm((prev) => ({
                        ...prev,
                        selectedFields: prev.selectedFields.filter(
                          (_, i) => i !== index
                        ),
                      }))
                    }
                  >
                    Remove
                  </Button>
                </div>
              ))}
            </Stack>
          </Card>

          <Card body className="card--md mb-3 report-builder-card">
            <h5 className="mb-3">Filters</h5>
            <Stack gap={2}>
              {form.filters.map((filter, index) => (
                <Row key={`filter-${index}`} className="g-2">
                  <Col md={3}>
                    <Form.Select
                      value={filter.fieldId}
                      onChange={(event) =>
                        setForm((prev) => {
                          const next = [...prev.filters]
                          const fieldId = event.target.value
                          const isMultiSelectFilter =
                            isMultiSelectFilterField(fieldId)
                          next[index] = {
                            ...next[index],
                            fieldId,
                            operator: isMultiSelectFilter
                              ? 'in'
                              : next[index].operator || 'eq',
                            value: isMultiSelectFilter ? [] : '',
                          }
                          return { ...prev, filters: next }
                        })
                      }
                    >
                      <option value="">Field</option>
                      {catalog.map((field) => (
                        <option key={field.id} value={field.id}>
                          {field.label}
                        </option>
                      ))}
                    </Form.Select>
                  </Col>
                  <Col md={3}>
                    <Form.Select
                      value={
                        isMultiSelectFilterField(filter.fieldId)
                          ? 'in'
                          : filter.operator
                      }
                      disabled={isMultiSelectFilterField(filter.fieldId)}
                      onChange={(event) =>
                        setForm((prev) => {
                          const next = [...prev.filters]
                          next[index] = {
                            ...next[index],
                            operator: event.target.value,
                          }
                          return { ...prev, filters: next }
                        })
                      }
                    >
                      {FILTER_OPERATORS.map((operator) => (
                        <option key={operator.value} value={operator.value}>
                          {operator.label}
                        </option>
                      ))}
                    </Form.Select>
                  </Col>
                  <Col md={5}>
                    {isMultiSelectFilterField(filter.fieldId) ? (
                      <>
                        <Form.Select
                          multiple
                          value={normalizeMultiSelectFilterValue(filter.value)}
                          onChange={(event) =>
                            setForm((prev) => {
                              const next = [...prev.filters]
                              next[index] = {
                                ...next[index],
                                value: Array.from(
                                  event.target.selectedOptions
                                ).map((option) => option.value),
                              }
                              return { ...prev, filters: next }
                            })
                          }
                          style={{ minHeight: 96 }}
                        >
                          {MULTI_SELECT_FILTER_OPTIONS_BY_FIELD[
                            filter.fieldId
                          ].map((option) => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </Form.Select>
                        <Form.Text className="text-muted">
                          Hold Cmd (Mac) or Ctrl (Windows) to select multiple
                          values.
                        </Form.Text>
                      </>
                    ) : isDateFilterField(filter.fieldId) &&
                      filter.operator !== 'between' ? (
                      <DatePicker
                        selected={parseDateFilterValue(filter.value)}
                        onChange={(date) =>
                          setForm((prev) => {
                            const next = [...prev.filters]
                            next[index] = {
                              ...next[index],
                              value: formatDateForFilter(date),
                            }
                            return { ...prev, filters: next }
                          })
                        }
                        disabled={
                          filter.operator === 'is_null' ||
                          filter.operator === 'not_null'
                        }
                        isClearable
                        dateFormat="MM/dd/yyyy"
                        placeholderText="MM/DD/YYYY"
                        className="form-control"
                      />
                    ) : (
                      <Form.Control
                        value={
                          Array.isArray(filter.value)
                            ? filter.value.join(', ')
                            : (filter.value ?? '')
                        }
                        disabled={
                          filter.operator === 'is_null' ||
                          filter.operator === 'not_null'
                        }
                        onChange={(event) =>
                          setForm((prev) => {
                            const next = [...prev.filters]
                            next[index] = {
                              ...next[index],
                              value: event.target.value,
                            }
                            return { ...prev, filters: next }
                          })
                        }
                        placeholder="Value"
                      />
                    )}
                  </Col>
                  <Col md={1}>
                    <Button
                      variant="outline-danger"
                      onClick={() =>
                        setForm((prev) => ({
                          ...prev,
                          filters: prev.filters.filter((_, i) => i !== index),
                        }))
                      }
                    >
                      X
                    </Button>
                  </Col>
                </Row>
              ))}
            </Stack>
            <Button
              variant="outline-primary"
              className="mt-3"
              onClick={() =>
                setForm((prev) => ({
                  ...prev,
                  filters: [...prev.filters, getDefaultFilter()],
                }))
              }
            >
              Add Filter
            </Button>
          </Card>

          <Card body className="card--md mb-3 report-builder-card">
            <h5 className="mb-3">Sorting, Grouping, Aggregations</h5>
            <Row className="g-3">
              <Col lg={4}>
                <Form.Label>Sort</Form.Label>
                <Stack gap={2}>
                  {form.sorts.map((sort, index) => (
                    <div key={`sort-${index}`} className="d-flex gap-2">
                      <Form.Select
                        value={sort.fieldId}
                        onChange={(event) =>
                          setForm((prev) => {
                            const next = [...prev.sorts]
                            next[index] = {
                              ...next[index],
                              fieldId: event.target.value,
                            }
                            return { ...prev, sorts: next }
                          })
                        }
                      >
                        <option value="">Field</option>
                        {catalog.map((field) => (
                          <option key={field.id} value={field.id}>
                            {field.label}
                          </option>
                        ))}
                      </Form.Select>
                      <Form.Select
                        value={sort.direction}
                        onChange={(event) =>
                          setForm((prev) => {
                            const next = [...prev.sorts]
                            next[index] = {
                              ...next[index],
                              direction: event.target.value,
                            }
                            return { ...prev, sorts: next }
                          })
                        }
                      >
                        {SORT_DIRECTIONS.map((dir) => (
                          <option key={dir.value} value={dir.value}>
                            {dir.label}
                          </option>
                        ))}
                      </Form.Select>
                    </div>
                  ))}
                  <Button
                    variant="outline-primary"
                    onClick={() =>
                      setForm((prev) => ({
                        ...prev,
                        sorts: [...prev.sorts, getDefaultSort()],
                      }))
                    }
                  >
                    Add Sort
                  </Button>
                </Stack>
              </Col>
              <Col lg={4}>
                <Form.Label>Group By</Form.Label>
                <Form.Text className="d-block mb-2 text-muted">
                  Selected fields not in Group By are auto-aggregated (sum for
                  numbers, count for text).
                </Form.Text>
                <Stack direction="horizontal" gap={2} className="mb-2">
                  <Button
                    variant="outline-primary"
                    size="sm"
                    disabled={!form.groupBy.length}
                    onClick={() =>
                      setForm((prev) => ({
                        ...prev,
                        groupBy: [],
                      }))
                    }
                  >
                    Clear Group By
                  </Button>
                </Stack>
                <Form.Select
                  multiple
                  value={form.groupBy}
                  onChange={(event) =>
                    setForm((prev) => ({
                      ...prev,
                      groupBy: Array.from(event.target.selectedOptions)
                        .map((option) => option.value)
                        .filter((fieldId) => String(fieldId || '').trim()),
                    }))
                  }
                  style={{ minHeight: 140 }}
                >
                  {catalog.map((field) => (
                    <option key={field.id} value={field.id}>
                      {field.label}
                    </option>
                  ))}
                </Form.Select>
              </Col>
              <Col lg={4}>
                <Form.Label>Aggregations</Form.Label>
                <Stack gap={2}>
                  {form.aggregations.map((aggregation, index) => (
                    <div key={`agg-${index}`} className="d-flex gap-2">
                      <Form.Select
                        value={aggregation.fieldId}
                        onChange={(event) =>
                          setForm((prev) => {
                            const next = [...prev.aggregations]
                            next[index] = {
                              ...next[index],
                              fieldId: event.target.value,
                            }
                            return { ...prev, aggregations: next }
                          })
                        }
                      >
                        <option value="">Field</option>
                        {catalog.map((field) => (
                          <option key={field.id} value={field.id}>
                            {field.label}
                          </option>
                        ))}
                      </Form.Select>
                      <Form.Select
                        value={aggregation.fn}
                        onChange={(event) =>
                          setForm((prev) => {
                            const next = [...prev.aggregations]
                            next[index] = {
                              ...next[index],
                              fn: event.target.value,
                            }
                            return { ...prev, aggregations: next }
                          })
                        }
                      >
                        {AGGREGATION_OPTIONS.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </Form.Select>
                    </div>
                  ))}
                  <Button
                    variant="outline-primary"
                    onClick={() =>
                      setForm((prev) => ({
                        ...prev,
                        aggregations: [
                          ...prev.aggregations,
                          getDefaultAggregation(),
                        ],
                      }))
                    }
                  >
                    Add Aggregation
                  </Button>
                </Stack>
              </Col>
            </Row>
          </Card>

          <Card body className="card--md mb-3 report-builder-card">
            <h5 className="mb-3">Schedule (Daily / Weekly Email)</h5>
            <Row className="g-2 align-items-end">
              <Col md={2}>
                <Form.Check
                  type="switch"
                  label="Enabled"
                  checked={Boolean(form.schedule?.enabled)}
                  onChange={(event) =>
                    setForm((prev) => ({
                      ...prev,
                      schedule: {
                        ...prev.schedule,
                        enabled: event.target.checked,
                      },
                    }))
                  }
                />
              </Col>
              <Col md={2}>
                <Form.Select
                  value={form.schedule?.frequency || 'daily'}
                  onChange={(event) =>
                    setForm((prev) => ({
                      ...prev,
                      schedule: {
                        ...prev.schedule,
                        frequency: event.target.value,
                      },
                    }))
                  }
                >
                  <option value="daily">Daily</option>
                  <option value="weekly">Weekly</option>
                </Form.Select>
              </Col>
              {form.schedule?.frequency === 'weekly' && (
                <Col md={2}>
                  <Form.Select
                    value={Number(form.schedule?.dayOfWeek ?? 1)}
                    onChange={(event) =>
                      setForm((prev) => ({
                        ...prev,
                        schedule: {
                          ...prev.schedule,
                          dayOfWeek: Number(event.target.value),
                        },
                      }))
                    }
                  >
                    {WEEK_DAY_OPTIONS.map((day) => (
                      <option key={day.value} value={day.value}>
                        {day.label}
                      </option>
                    ))}
                  </Form.Select>
                </Col>
              )}
              <Col md={2}>
                <Form.Control
                  type="number"
                  min={0}
                  max={23}
                  value={form.schedule?.hour ?? 8}
                  onChange={(event) =>
                    setForm((prev) => ({
                      ...prev,
                      schedule: {
                        ...prev.schedule,
                        hour: Number(event.target.value),
                      },
                    }))
                  }
                  placeholder="Hour"
                />
              </Col>
              <Col md={2}>
                <Form.Control
                  type="number"
                  min={0}
                  max={59}
                  value={form.schedule?.minute ?? 0}
                  onChange={(event) =>
                    setForm((prev) => ({
                      ...prev,
                      schedule: {
                        ...prev.schedule,
                        minute: Number(event.target.value),
                      },
                    }))
                  }
                  placeholder="Minute"
                />
              </Col>
              <Col md={2}>
                <Form.Control
                  value={form.schedule?.email || ''}
                  onChange={(event) =>
                    setForm((prev) => ({
                      ...prev,
                      schedule: { ...prev.schedule, email: event.target.value },
                    }))
                  }
                  placeholder="Email"
                />
              </Col>
            </Row>
          </Card>

          <Card body className="card--md mb-3 report-builder-card">
            <Stack
              direction="horizontal"
              gap={2}
              className="flex-wrap report-builder-actions"
            >
              <Button
                className="report-builder__primary-btn"
                disabled={isGenerating}
                onClick={handlePreview}
              >
                {previewing ? 'Loading...' : 'Preview'}
              </Button>
              <ReportRefreshButton
                onRefresh={refresh}
                isRefreshing={previewing}
                disabled={
                  previewing || (!previewRows.length && !selectedReportId)
                }
              />
              <Button
                className="report-builder__success-btn"
                variant="success"
                disabled={saving || isGenerating}
                onClick={handleSave}
              >
                {saving
                  ? 'Saving...'
                  : selectedReportId
                    ? 'Update Report'
                    : 'Save Report'}
              </Button>
              {selectedReportId && (
                <Button
                  className="report-builder__outline-btn"
                  variant="outline-primary"
                  disabled={isGenerating}
                  onClick={handleRunSaved}
                >
                  Run Saved
                </Button>
              )}
              <Button
                className="report-builder__outline-btn"
                variant="outline-primary"
                disabled={isGenerating}
                onClick={() => handleExport('csv')}
              >
                Export CSV
              </Button>
              <Button
                className="report-builder__outline-btn"
                variant="outline-primary"
                disabled={isGenerating}
                onClick={() => handleExport('xlsx')}
              >
                Export Excel
              </Button>
              <Button
                className="report-builder__outline-btn"
                variant="outline-primary"
                disabled={isGenerating}
                onClick={() => handleExport('pdf')}
              >
                Export PDF
              </Button>
            </Stack>
          </Card>

          <Card
            body
            className="card--md report-builder-card report-builder-card--preview"
            id="report-builder-preview"
          >
            <h5 className="mb-3">Live Preview (first 100 rows)</h5>
            {!previewRows.length ? (
              <span className="text-muted">
                No preview data yet. Run Preview to load rows.
              </span>
            ) : (
              <div className="table-container border-bottom-0">
                <div className="table-container--fixed-height">
                  <div className="table-responsive">
                    <Table
                      bordered
                      hover
                      size="sm"
                      className="report-builder-preview-table"
                    >
                      <thead>
                        <tr>
                          {Object.keys(previewRows[0]).map((header) => (
                            <th key={header}>{header}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {previewRows.map((row, rowIndex) => (
                          <tr key={`row-${rowIndex}`}>
                            {Object.keys(previewRows[0]).map((header) => (
                              <td key={`${rowIndex}-${header}`}>
                                {formatCellValue(row[header])}
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                </div>
              </div>
            )}
          </Card>
        </Col>
      </Row>
    </section>
  )
}
